import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV, BRAND } from "../data/content";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "glass py-3" : "bg-transparent py-5"
        }`}
      >
        <nav className="container-x flex items-center justify-between">
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center"
          >
            <img
              src={BRAND.logo}
              alt="Ideal Entertainment"
              className="h-9 w-auto object-contain md:h-11"
            />
          </Link>

          <ul className="hidden lg:flex items-center gap-7">
            {NAV.map((item) => {
              const active = location.pathname === item.path;
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    data-testid={`nav-link-${item.label.toLowerCase()}`}
                    className={`relative text-sm font-light tracking-wide transition-colors duration-300 ${
                      active ? "text-white" : "text-neutral-300 hover:text-white"
                    }`}
                  >
                    {item.label}
                    {active && (
                      <span className="absolute -bottom-2 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-gold" />
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>

          <div className="hidden lg:block">
            <Link
              to="/contact"
              data-testid="nav-cta-button"
              className="rounded-full border border-white/20 px-6 py-2.5 text-sm tracking-wide transition-all duration-300 hover:bg-white hover:text-black"
            >
              Start a Project
            </Link>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-white"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 lg:hidden glass pt-24"
          >
            <ul className="container-x flex flex-col gap-2">
              {NAV.map((item, i) => (
                <motion.li
                  key={item.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Link
                    to={item.path}
                    data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                    className="block border-b border-white/5 py-4 font-playfair text-2xl text-white"
                  >
                    {item.label}
                  </Link>
                </motion.li>
              ))}
              <Link
                to="/contact"
                className="mt-6 rounded-full bg-gold px-6 py-3 text-center text-sm font-medium text-black"
              >
                Start a Project
              </Link>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
